import { logger } from '@/engine/logging';
import * as PIXI from 'pixi.js';
import type { ParticleProperties, ResolvedParticleData } from './ParticleProperties';
import { resolveValue, Easing } from './ParticleTypes';
import { Particle } from './Particle';
import { buildRuntimeAtlas, destroyRuntimeAtlas, type RuntimeAtlas } from './runtimeAtlas';

/**
 * Высокопроизводительная система частиц на основе пула объектов.
 * Предназначена для управления большим количеством короткоживущих спрайтов.
 */
export class ParticleSystem {
  private readonly _particleContainer: PIXI.ParticleContainer;
  private readonly _pool: Particle[] = [];
  private readonly _activeParticles: Particle[] = [];
  private readonly _textures = new Map<string, PIXI.Texture>();
  private readonly _maxParticles: number;
  private readonly _renderer: PIXI.Renderer;
  private readonly _log = logger.child({
    source: 'render',
    fields: { system: 'ParticleSystem' },
  });
  private _atlas: RuntimeAtlas | null = null;

  private _isRunning: boolean = false;
  private _frameBudget = 500; // сколько частиц максимум за один кадр
  private _spawnedThisFrame = 0;

  constructor(parent: PIXI.Container, renderer: PIXI.Renderer, maxParticles: number = 10000) {
    this._maxParticles = maxParticles;
    this._renderer = renderer;

    this._particleContainer = new PIXI.ParticleContainer({
      dynamicProperties: {
        position: true,
        scaleX: true,
        scaleY: true,
        rotation: true,
        tint: true, // В Pixi v8 свойство называется tint, а не color
        alpha: true,
      },
    });

    this._particleContainer.blendMode = 'add'; // Лучше для неоновых эффектов
    parent.addChild(this._particleContainer);

    // Не трогаем bounds — дорого и не нужно

    this._buildParticleAtlas();
    // Предварительно заполняем пул, чтобы избежать аллокаций в "горячем" коде
    this._growPool(Math.min(500, this._maxParticles));
  }

  // --- Управление жизненным циклом системы ---

  public start(): void {
    this._isRunning = true;
  }
  public stop(): void {
    this._isRunning = false;
    this.clear();
  }
  public get isRunning(): boolean {
    return this._isRunning;
  }
  public get activeParticleCount(): number {
    return this._activeParticles.length;
  }
  public getParticleCount(): number {
    return this._activeParticles.length;
  } // Обратная совместимость

  public update(deltaTime: number): void {
    if (!this._isRunning || this._activeParticles.length === 0) {
      return;
    }

    // Сбрасываем счетчик частиц для нового кадра
    this._spawnedThisFrame = 0;

    let removedCount = 0;
    // Итерируем с конца, чтобы безопасно удалять элементы.
    for (let i = this._activeParticles.length - 1; i >= 0; i--) {
      const particle = this._activeParticles[i];

      if (!particle.update(deltaTime)) {
        // SENIOR: Используем "swap and pop" вместо `splice` для максимальной производительности.
        // Это операция O(1) вместо O(n) у splice.
        const lastParticle = this._activeParticles.pop()!;
        if (i < this._activeParticles.length) {
          // Если удаляемый не был последним
          this._activeParticles[i] = lastParticle;
        }

        // Возвращаем объект обратно в пул
        this._particleContainer.removeParticle(particle.particle);
        particle.reset();
        this._pool.push(particle);
        removedCount++;
      }
    }
  }

  /**
   * Emit particles using ParticleProperties system
   */
  public emit(properties: ParticleProperties, x: number = 0, y: number = 0): void {
    if (!this._isRunning) {
      this._log.warn('System is not running, cannot emit particles');
      return;
    }

    // Set blend mode for this emission
    //this._particleContainer.blendMode = resolvedData.blendMode as any;

    const requestedCount = resolveValue(properties.count ?? 1);
    let count = Math.min(requestedCount, this._maxParticles - this._activeParticles.length);

    // бюджет кадра
    const left = Math.max(0, this._frameBudget - this._spawnedThisFrame);
    count = Math.min(count, left);

    if (count <= 0) {
      return; // Без логов - это нормальное поведение при превышении бюджета
    }

    let emittedCount = 0;
    let loggedTexture: string | null = null;
    for (let i = 0; i < count; i++) {
      let particle = this._pool.pop();

      if (!particle) {
        this._growPool();
        particle = this._pool.pop();
        if (!particle) break;
      }

      const particleData = this._resolveParticleProperties(properties);
      if (!particleData) {
        this._log.warn('Failed to resolve particle properties for emitted particle', {
          particleIndex: i,
        });
        this._pool.push(particle);
        continue;
      }

      if (!loggedTexture) {
        loggedTexture = typeof particleData.texture === 'string' ? particleData.texture : null;
      }

      particle.init(particleData, x, y);
      this._activeParticles.push(particle);
      this._particleContainer.addParticle(particle.particle);
      emittedCount++;
    }

    // без принудительных bounds
    this._spawnedThisFrame += emittedCount;

    // Обновляем контейнер после добавления частиц
    this._particleContainer.update();
  }

  // --- Утилиты и управление ---

  public clear(): void {
    while (this._activeParticles.length > 0) {
      const particle = this._activeParticles.pop()!;
      particle.isActive = false;
      this._particleContainer.removeParticle(particle.particle);
      this._pool.push(particle);
    }
  }

  public destroy(): void {
    this.stop();
    this._pool.length = 0; // Очищаем массив пула
    this._particleContainer.destroy({ children: true, texture: false });

    // Уничтожаем атлас
    if (this._atlas) {
      destroyRuntimeAtlas(this._atlas);
      this._atlas = null;
    }

    this._textures.clear();
  }

  // --- Управление текстурами ---

  public getAvailableTextures(): string[] {
    return Array.from(this._textures.keys());
  }

  /**
   * Добавляет кастомную текстуру в систему
   * ВНИМАНИЕ: Кастомные текстуры НЕ будут работать в одном ParticleContainer с атласными
   * Для смешивания нужно пересоздать атлас с дополнительными формами
   */
  public addCustomTexture(id: string, texture: PIXI.Texture): void {
    console.warn(
      `ParticleSystem: Adding custom texture "${id}" - it won't work with atlas textures in same container`
    );
    this._textures.set(id, texture);
  }

  private _growPool(amount?: number): void {
    // Растем на 20% от максимального числа частиц или на указанное число
    const growthAmount = amount ?? Math.ceil(this._maxParticles * 0.2);

    for (let i = 0; i < growthAmount; i++) {
      if (this._pool.length + this._activeParticles.length >= this._maxParticles) break;
      this._pool.push(new Particle());
    }
  }

  /**
   * Строит runtime-атлас для частиц
   * Все формы рендерятся в один RenderTexture с общим baseTexture
   * Это позволяет ParticleContainer смешивать разные типы частиц
   */
  private _buildParticleAtlas(): void {
    this._atlas = buildRuntimeAtlas(this._renderer, {
      tileSize: 64, // размер клетки под форму
      padding: 2, // защита от bleeding
      cols: 4, // кол-во колонок в атласе
    });

    // Копируем текстуры из атласа в наш Map
    this._textures.clear();
    for (const [id, tex] of this._atlas.textures) {
      this._textures.set(id, tex);
    }

    // Бонус: алиасы для совместимости
    if (!this._textures.has('default')) {
      // если нет default, используем soft_circle
      this._textures.set('default', this._textures.get('soft_circle')!);
    }

    this._log.info('Built particle atlas', {
      total: this._textures.size,
      textures: Array.from(this._textures.keys()),
    });
  }

  /**
   * Resolves ParticleProperties to concrete ResolvedParticleData
   */
  private _resolveParticleProperties(properties: ParticleProperties): ResolvedParticleData | null {
    try {
      // Resolve texture
      const textureValue = properties.texture ? resolveValue(properties.texture) : 'default';
      const texture =
        typeof textureValue === 'string'
          ? this._textures.get(textureValue) ||
            this._textures.get('default') ||
            this._createFallbackTexture()
          : textureValue;

      // Debug: log texture usage
      if (typeof textureValue === 'string' && !this._textures.has(textureValue)) {
        this._log.warn('Texture not found, falling back to default', {
          requestedTexture: textureValue,
        });
      }

      // Resolve lifetime
      const lifetime = resolveValue(properties.lifetime);

      // Resolve angle and speed
      const angle = resolveValue(properties.angle ?? 0);
      const speedStart = properties.speed ? resolveValue(properties.speed.start) : 100;
      const speedEnd = properties.speed
        ? resolveValue(
            properties.speed.end !== undefined ? properties.speed.end : properties.speed.start
          )
        : speedStart;
      const speedEase = properties.speed?.ease || Easing.linear;

      // Calculate initial velocity
      const vx = Math.cos(angle) * speedStart;
      const vy = Math.sin(angle) * speedStart;

      // Resolve scale
      const scaleStart = resolveValue(properties.scale?.start ?? 1);
      const scaleEnd = resolveValue(
        properties.scale?.end !== undefined ? properties.scale.end : (properties.scale?.start ?? 1)
      );
      const scaleEase = properties.scale?.ease || Easing.linear;

      // Resolve color
      const pickColor = (v: any): number => {
        if (Array.isArray(v)) {
          if (v.length === 2) return resolveValue(v as [number, number]); // диапазон
          const idx = (Math.random() * v.length) | 0; // выбор из палитры
          return v[idx];
        }
        return v ?? 0xffffff;
      };
      const colorStart = pickColor(properties.color?.start);
      const colorEnd = pickColor(
        properties.color?.end !== undefined ? properties.color.end : properties.color?.start
      );
      const colorEase = properties.color?.ease || Easing.linear;

      // Resolve alpha - FIX: use !== undefined to allow 0 as valid value
      const alphaStart = resolveValue(properties.alpha?.start ?? 1);
      const alphaEnd = resolveValue(
        properties.alpha?.end !== undefined ? properties.alpha.end : (properties.alpha?.start ?? 0)
      );
      const alphaEase = properties.alpha?.ease || Easing.linear;

      // Resolve acceleration
      const accelX = resolveValue(properties.acceleration?.x ?? 0);
      const accelY = resolveValue(properties.acceleration?.y ?? 0);

      // Resolve other properties
      const friction = resolveValue(properties.friction ?? 0.98);
      const mass = resolveValue(properties.mass ?? 1);
      const bounce = resolveValue(properties.bounce ?? 0);
      const initialRotation = resolveValue(properties.initialRotation ?? 0);
      const rotationSpeed = resolveValue(properties.rotationSpeed ?? 0);
      const blendMode = properties.blendMode || 'add';

      // Resolve oscillation
      const oscillation = properties.oscillation
        ? {
            frequency: resolveValue(properties.oscillation.frequency),
            amplitude: resolveValue(properties.oscillation.amplitude),
          }
        : undefined;

      // Resolve follow target
      const followTarget = properties.followTarget
        ? {
            x: properties.followTarget.x,
            y: properties.followTarget.y,
            strength: resolveValue(properties.followTarget.strength),
            maxTurnRate: resolveValue(properties.followTarget.maxTurnRate),
          }
        : undefined;

      // Resolve orbit
      const orbit = properties.orbit
        ? {
            centerX: properties.orbit.centerX,
            centerY: properties.orbit.centerY,
            radius: resolveValue(properties.orbit.radius),
            speed: resolveValue(properties.orbit.speed),
            maintainRadius: properties.orbit.maintainRadius,
          }
        : undefined;

      // Resolve trail
      const trail = properties.trail
        ? {
            properties: this._resolveParticleProperties(
              properties.trail.properties as ParticleProperties
            )!,
            spacing: resolveValue(properties.trail.spacing),
            lifetime: resolveValue(properties.trail.lifetime),
          }
        : undefined;

      // Resolve glow
      const glow = properties.glow
        ? {
            color: resolveValue(properties.glow.color),
            intensity: resolveValue(properties.glow.intensity),
            size: resolveValue(properties.glow.size),
          }
        : undefined;

      return {
        texture,
        lifetime,
        x: 0, // Will be set by initAdvanced
        y: 0, // Will be set by initAdvanced
        vx,
        vy,
        speed: {
          start: speedStart,
          end: speedEnd,
          ease: speedEase,
        },
        scale: {
          start: scaleStart,
          end: scaleEnd,
          ease: scaleEase,
        },
        rotation: {
          initial: initialRotation,
          speed: rotationSpeed,
        },
        color: {
          start: colorStart,
          end: colorEnd,
          ease: colorEase,
        },
        alpha: {
          start: alphaStart,
          end: alphaEnd,
          ease: alphaEase,
        },
        acceleration: {
          x: accelX,
          y: accelY,
        },
        friction,
        mass,
        bounce,
        oscillation,
        followTarget,
        orbit,
        trail,
        glow,
        blendMode,
      };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this._log.error('Failed to resolve particle properties', err);
      return null;
    }
  }

  /**
   * Creates a fallback texture using the same baseTexture as the atlas
   */
  private _createFallbackTexture(): PIXI.Texture {
    if (this._atlas) {
      // Use the same baseTexture as the atlas, but with a default frame
      return new PIXI.Texture({
        source: this._atlas.baseTexture,
        frame: new PIXI.Rectangle(0, 0, 32, 32), // Default frame
      });
    }
    return PIXI.Texture.WHITE;
  }
}
